import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Popover, List, ListItem, ListItemText,
  Divider, IconButton, Tooltip, Chip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../utils/axios';

// One badge in the sidebar that covers EVERY "who's on the site" signal:
//
//   • Recipients actively watching (per-tenant if the user is regular,
//     cross-tenant if superadmin)
//   • Recipients actively downloading
//   • Other authenticated operators on the site (superadmin only)
//
// Pre-v10.17.12 we shipped three different badges for these — top bar,
// footer, and sidebar — each with its own popover. Operator asked to
// consolidate into a single sidebar pill because that's the place they
// naturally look. The role split is collapsed into one badge with
// labeled sections in the popover.
//
// Renders nothing when the total count is zero. Footer's "Nobody else
// here · safe to deploy" affordance is gone — operators just won't see
// the pill at all when nobody is around, which is the same information.

const POLL_INTERVAL_MS = 15_000;
const PRESENCE_WINDOW_SECONDS = 120;

function fmtPosition(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtAgo(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ago`;
}

function fmtAgoFromIso(iso) {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

export default function SitePresenceBeacon({ collapsed }) {
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const isSuperadmin = !!userInfo?.is_superadmin;

  const [watchers, setWatchers] = useState([]);
  const [downloaders, setDownloaders] = useState([]);
  const [operators, setOperators] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      // Recipient feed: superadmins see the cross-tenant /global view;
      // regular users see only their active company's view. Both endpoints
      // shape the data identically.
      const recipientUrl = isSuperadmin
        ? '/api/jobs/live-activity/global'
        : '/api/jobs/live-activity';
      try {
        const r = await api.get(recipientUrl);
        if (cancelled) return;
        setWatchers(r.data?.watchers || []);
        setDownloaders(r.data?.downloaders || []);
      } catch (_) {
        // transient — keep last value so the pill doesn't flash
      }

      // Operator feed is superadmin-only. Skip the call entirely for
      // regular users — the endpoint would 403 and surface as a logout
      // path via the response interceptor.
      if (!isSuperadmin) {
        setOperators([]);
        return;
      }
      try {
        const r = await api.get(
          `/api/operator/presence?window_seconds=${PRESENCE_WINDOW_SECONDS}`,
        );
        if (!cancelled) setOperators(Array.isArray(r.data) ? r.data : []);
      } catch (_) {
        /* keep last value */
      }
    }

    poll();
    timerRef.current = setInterval(() => {
      if (!document.hidden) poll();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isSuperadmin]);

  const total = watchers.length + downloaders.length + operators.length;

  // Static pill — always rendered so the sidebar layout doesn't shift
  // when someone arrives / leaves. Three palettes:
  //   recipients present → green + pulse (high-stakes: don't disrupt)
  //   operators only     → gold + pulse  (safe-to-deploy signal)
  //   nobody on site     → neutral muted (no pulse, no popover open)
  const recipientCount = watchers.length + downloaders.length;
  const isRecipientActive = recipientCount > 0;
  const isEmpty = total === 0;
  const palette = isEmpty
    ? { fg: 'rgba(232,230,227,0.45)', bg: 'rgba(255,255,255,0.02)', border: 'rgba(232,230,227,0.10)', glow: null }
    : isRecipientActive
      ? { fg: '#66bb6a', bg: 'rgba(102,187,106,0.08)', border: 'rgba(102,187,106,0.28)', glow: 'rgba(102,187,106,0.7)' }
      : { fg: '#d4a017', bg: 'rgba(212,160,23,0.08)', border: 'rgba(212,160,23,0.28)', glow: 'rgba(212,160,23,0.7)' };

  const tooltipText = (() => {
    if (isEmpty) return 'Nobody on the site.';
    const parts = [];
    if (watchers.length) parts.push(`${watchers.length} watching`);
    if (downloaders.length) parts.push(`${downloaders.length} downloading`);
    if (operators.length) parts.push(`${operators.length} operator${operators.length === 1 ? '' : 's'}`);
    return `${parts.join(' · ')} — click for details`;
  })();

  const label = isEmpty ? 'Nobody on site' : `${total} on site`;
  const open = Boolean(anchorEl) && !isEmpty;

  return (
    <>
      <Tooltip title={tooltipText} placement="right" arrow>
        <Box
          onClick={isEmpty ? undefined : (e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.75, mb: 0.5,
            borderRadius: 1.5, cursor: isEmpty ? 'default' : 'pointer',
            justifyContent: collapsed ? 'center' : 'flex-start',
            bgcolor: palette.bg,
            border: `1px solid ${palette.border}`,
            ...(isEmpty ? {} : { '&:hover': { bgcolor: palette.bg.replace('0.08', '0.16') } }),
          }}
        >
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ fontSize: 16, color: palette.fg }} />
            {!isEmpty && palette.glow && (
              <Box
                sx={{
                  position: 'absolute', top: -2, right: -2,
                  width: 6, height: 6, borderRadius: '50%', bgcolor: palette.fg,
                  boxShadow: `0 0 0 0 ${palette.glow}`,
                  animation: 'site-presence-pulse 1.6s ease-in-out infinite',
                  '@keyframes site-presence-pulse': {
                    '0%':   { boxShadow: `0 0 0 0 ${palette.glow}` },
                    '70%':  { boxShadow: `0 0 0 6px ${palette.glow.replace('0.7', '0')}` },
                    '100%': { boxShadow: `0 0 0 0 ${palette.glow.replace('0.7', '0')}` },
                  },
                }}
              />
            )}
          </Box>
          {!collapsed && (
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', fontWeight: 500, color: palette.fg, lineHeight: 1.2 }}
            >
              {label}
            </Typography>
          )}
        </Box>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{ sx: { ml: 1, minWidth: 340, maxWidth: 440, borderRadius: 2 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="overline" sx={{ fontSize: '0.65rem', color: 'text.secondary', letterSpacing: 0.8 }}>
            On the site right now
          </Typography>
          {isSuperadmin && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.25 }}
            >
              Check before pushing a deploy.
            </Typography>
          )}
        </Box>
        <Divider />

        {watchers.length > 0 && (
          <>
            <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <VisibilityIcon sx={{ fontSize: 13, color: '#66bb6a' }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Watching ({watchers.length})
              </Typography>
            </Box>
            <List dense sx={{ py: 0 }}>
              {watchers.map((w, i) => (
                <ListItem
                  key={w.session_id}
                  divider={i < watchers.length - 1}
                  secondaryAction={
                    w.watch_url ? (
                      <Tooltip title="Open Follow Viewer in new tab" arrow>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            window.open(w.watch_url, '_blank', 'noopener,noreferrer');
                            setAnchorEl(null);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                  sx={{ py: 0.85 }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }} noWrap>
                        {w.recipient_name || 'Unknown recipient'}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }} noWrap>
                          {[w.company_short_name || w.company_name, w.source_filename].filter(Boolean).join(' · ')}
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: '#66bb6a', fontSize: '0.7rem' }}>
                          {fmtPosition(w.position_seconds)} in · {Math.round(w.completion_percent)}%
                          {w.started_at ? ` · started ${fmtAgoFromIso(w.started_at)}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {downloaders.length > 0 && (
          <>
            <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <DownloadIcon sx={{ fontSize: 13, color: '#66bb6a' }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Downloading ({downloaders.length})
              </Typography>
            </Box>
            <List dense sx={{ py: 0 }}>
              {downloaders.map((d, i) => (
                <ListItem key={d.event_id || `${d.recipient_name}-${i}`} divider={i < downloaders.length - 1} sx={{ py: 0.85 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }} noWrap>
                        {d.recipient_name || 'Unknown recipient'}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }} noWrap>
                          {d.source_filename}
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: '#66bb6a', fontSize: '0.7rem' }}>
                          Started {fmtAgoFromIso(d.started_at)}
                          {d.ip_address ? ` · ${d.ip_address}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {operators.length > 0 && (
          <>
            <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PeopleIcon sx={{ fontSize: 13, color: '#d4a017' }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Other operators ({operators.length})
              </Typography>
            </Box>
            <List dense sx={{ py: 0 }}>
              {operators.map((u, i) => (
                <ListItem key={u.user_id} divider={i < operators.length - 1} sx={{ py: 0.85 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }} noWrap>
                          {u.full_name || u.username}
                        </Typography>
                        {u.is_superadmin && (
                          <Chip
                            size="small"
                            label="HHMG"
                            sx={{
                              height: 16, fontSize: '0.62rem', fontWeight: 700,
                              bgcolor: 'rgba(212,160,23,0.18)', color: '#d4a017',
                              '& .MuiChip-label': { px: 0.6 },
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }} noWrap>
                          {u.role_label} · {u.email || u.username}
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ display: 'block', color: '#d4a017', fontSize: '0.7rem' }}>
                          last active {fmtAgo(u.seconds_ago)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Popover>
    </>
  );
}
